import { useRequireAuth } from "lib/use-auth";
import { HumanEvaluator } from "@/types/app/evaluator";
import { EvaluatorLog, Log } from "@/types/app/log";
import { createEvaluatorLogAndFetchLog } from "@/services/evaluators.service";
import { SelectInput } from "@components/molecules/FormikInput";
import { SelectItem } from "@components/ui/select";
import { Tag } from "@library/Tag";
import React, { useState } from "react";
import { classNames } from "@/lib/utils";

interface Props {
  evaluator: HumanEvaluator;
  latestFeedback: Log | null;
  addingFeedback: boolean;
  placeholder?: string;
  setAddingFeedback: (addingFeedback: boolean) => void;
  onError: (error: string) => void;
  onAddFeedback: (evaluatorLog: EvaluatorLog) => void;
  logId: string;
  className?: string;
}

const FeedbackSelect = ({
  evaluator,
  latestFeedback,
  addingFeedback,
  placeholder,
  setAddingFeedback,
  onAddFeedback,
  className,
  onError,
  logId,
}: Props) => {
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    latestFeedback && "judgment" in latestFeedback ? (latestFeedback.judgment ?? "") : "",
  );

  const { user } = useRequireAuth();
  return (
    <div className="flex flex-grow flex-col">
      <SelectInput
        name="feedback"
        placeholder={placeholder ?? "None"}
        size={24}
        className={classNames("", className)}
        disabled={addingFeedback}
        value={currentValue}
        setValue={async (value) => {
          if (!value) return;
          setAddingFeedback(true);

          try {
            setCurrentValue(value);
            const createdLog = await createEvaluatorLogAndFetchLog(
              {
                id: evaluator.id,
                parent_id: logId,
                judgment: value,
                user: user?.email_address,
              },
              evaluator.version_id,
            );
            onAddFeedback(createdLog);
          } catch (error) {
            setCurrentValue(currentValue);
            onError("Failed to update field. Please try again");
          } finally {
            setAddingFeedback(false);
          }
        }}
      >
        {evaluator.spec.options?.map((option) => {
          const valence = option.valence;
          const shade = valence === "positive" ? "green" : valence === "negative" ? "red" : "gray";

          return (
            <SelectItem key={option.name} value={option.name}>
              <div className="flex items-center justify-center gap-8">
                <Tag color={shade}>{option.name}</Tag>
              </div>
            </SelectItem>
          );
        })}
      </SelectInput>
    </div>
  );
};

export default FeedbackSelect;
